import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Button from '@mui/material/Button';

import {
  dateStringForDateTime,
  isNonLocalNonLoggedInVisitor,
  getSpecifiedFilterMenuItems,
  getPropertyFilterMenuItems,
  getDefaultPropertyFilterMenuItems,
  getUserFilterMenuItems,
  getDefaultUserFilterMenuItems,
  allMenuItemsAreChecked,
  filterIsInDefaultState,
} from '../utils/utils';

import {
  setIntendedReferrerContactId,
  setIntendedReferrerCompanyId,
  updateContactsFilter,
} from '../store/slices/intendedInfoSlice';
import { setPropertyFilterState, setPersonFilterState } from '../store/slices/filterStatesSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import SocketAccess from '../comm/SocketAccess';

import DA from '../store/ReduxAccess';

import IcfUnaffiliatedContactModal from './IcfUnaffiliatedContactModal';
import ContactTableAppBar from './ContactTableAppBar';
import ColumnFilterIcon from './ColumnFilterIcon';

import pageTableStyles from '../assets/jss/crm/components/pageTableStyles.jsx';
import tableHeadStyles from '../assets/jss/crm/components/tableHeadStyles.jsx';

function desc(a, b, orderBy) {
  let aValue = a[orderBy];
  let bValue = b[orderBy];
  if (typeof aValue === 'string') {
    aValue = aValue.toLowerCase();
    bValue = bValue.toLowerCase();
  }
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function maybeSplitAndStableSort(array, cmp, orderBy) {
  if (orderBy === 'companyName' || orderBy === 'firmType') {
    const affiliatedContacts = array.filter((el) => el.companyName !== '[None]');
    const unaffiliatedContacts = array.filter((el) => el.companyName === '[None]');
    const sortedAffiliated = stableSort(affiliatedContacts, cmp);
    const sortedUnaffiliated = stableSort(unaffiliatedContacts, cmp);
    return [...sortedAffiliated, ...sortedUnaffiliated];
  } else {
    return stableSort(array, cmp);
  }
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const columns = [
  { id: 'name', label: 'Name' },
  { id: 'firmType', filterable: true, label: 'Company Category' },
  { id: 'companyName', label: 'Company' },
  { id: 'ownerName', filterable: true, label: 'Owner' },
  { id: 'temperature', filterable: true, label: 'Temp' },
  { id: 'active', filterable: true, label: 'Active?' },
  { id: 'daysSince', numeric: true, label: 'Inactive Days' },
  { id: 'lastContact', numeric: true, label: 'Last Contact' },
];

function handleEntered() {
  document.getElementsByTagName('body')[0].style.paddingRight = '0px';
}

class ContactTableHead extends React.Component {
  createSortHandler = (property) => (event) => {
    this.props.onRequestSort(event, property);
  };

  createFilterHandler = (property) => (event) => {
    this.props.onRequestFilter(event, property);
  };

  render() {
    const { classes, order, orderBy, shouldHighlightFilterIcon } = this.props;

    return (
      <TableHead>
        <TableRow className={classes.headerRow}>
          {columns.map(
            (column) => (
              <TableCell
                key={column.id}
                align={column.numeric ? 'right' : 'left'}
                sortDirection={orderBy === column.id ? order : false}
              >
                {column.filterable && (
                  <ColumnFilterIcon
                    onClick={this.createFilterHandler(column.id)}
                    shouldHighlight={() => shouldHighlightFilterIcon(column.id)}
                  />
                )}

                {column.label}

                {!column.notSortable && (
                  <Tooltip title="Sort" placement={column.numeric ? 'bottom-end' : 'bottom-start'} enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={this.createSortHandler(column.id)}
                      classes={{
                        icon: orderBy === column.id ? classes.activeSortIcon : classes.inactiveSortIcon,
                      }}
                    />
                  </Tooltip>
                )}
              </TableCell>
            ),
            this
          )}
        </TableRow>
      </TableHead>
    );
  }
}

ContactTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  onRequestFilter: PropTypes.func.isRequired,
};

const StyledTableHead = withStyles(tableHeadStyles)(ContactTableHead);

const styles = (theme) => ({
  ...pageTableStyles(theme),
  inactiveName: {
    color: '#aaa',
  },
  ownerListItem: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  selectAllButton: {
    textTransform: 'none',
    fontSize: '18px',
  },
  ownerLabel: {
    color: '#6b6b6b',
  },
});

class ContactTable extends React.Component {
  state = {
    order: 'asc',
    orderBy: 'name',
    autocompleteValue: '',
    showNewContactModal: false,
    showFilter: false,
    filterItemList: [],
    filterBy: '',
    ...this.props.reduxFilterMenuState,
  };

  firmTypeMenuItems = this.getFirmTypeMenuItems();
  ownerMenuItems = this.getOwnerMenuItems();
  temperatureMenuItems = this.getTemperatureMenuItems();
  activeMenuItems = this.getActiveMenuItems();

  defaultFirmTypeMenuItems = this.getDefaultMenuItems('Referrer Organization Type');
  defaultOwnerMenuItems = this.getDefaultMenuItems('Owner');
  defaultTemperatureMenuItems = this.getDefaultMenuItems('Referral Contact Temperature');
  defaultActiveMenuItems = [
    {
      name: 'Active',
      checked: true,
    },
    {
      name: 'Inactive',
      checked: false,
    },
  ];

  componentDidMount() {
    this.firmTypeMenuItems = this.getFirmTypeMenuItems();
    this.ownerMenuItems = this.getOwnerMenuItems();
    this.temperatureMenuItems = this.getTemperatureMenuItems();
    this.activeMenuItems = this.getActiveMenuItems();

    const callbackStruct = {
      kind: 'referralContact',
      pageName: 'ContactTable',
      refreshFunc: () => this.redisplayTable(),
    };
    SocketAccess.registerRefreshCallback(callbackStruct);
  }

  componentWillUnmount() {
    SocketAccess.unregisterRefreshCallbacksForPage('ContactTable');
  }

  redisplayTable() {
    this.setState({ redisplayToggle: !this.state.redisplayToggle });
  }

  getFirmTypeMenuItems() {
    const pageFiltersState = this.props.reduxPropertyFiltersState['referralContactsPage'];
    const menuMentionedItems = getPropertyFilterMenuItems(pageFiltersState, 'firmType', 'Referrer Organization Type');
    return [...menuMentionedItems, { name: '[None]', checked: true }];
  }

  getOwnerMenuItems() {
    const pageFiltersState = this.props.reduxPropertyFiltersState['referralContactsPage'];
    return getUserFilterMenuItems(pageFiltersState, 'owner');
  }

  getTemperatureMenuItems() {
    const pageFiltersState = this.props.reduxPropertyFiltersState['referralContactsPage'];
    return getPropertyFilterMenuItems(pageFiltersState, 'temperature', 'Referral Contact Temperature');
  }

  getActiveMenuItems() {
    const pageFiltersState = this.props.reduxPropertyFiltersState['referralContactsPage'];
    const defaultOptions = [
      { name: 'Active', value: true },
      { name: 'Inactive', value: false },
    ];
    return getSpecifiedFilterMenuItems(pageFiltersState, 'active', defaultOptions);
  }

  getDefaultMenuItems(dropdownName) {
    if (dropdownName !== 'Owner') {
      return getDefaultPropertyFilterMenuItems(dropdownName);
    } else {
      return getDefaultUserFilterMenuItems();
    }
  }

  isFirmTypeMenuItemChecked(firmType) {
    const matchingFirmTypeItem = this.firmTypeMenuItems.find((item) => item.name === firmType);
    return matchingFirmTypeItem && matchingFirmTypeItem.checked;
  }

  isOwnerMenuItemChecked(ownerId) {
    const foundOwnerItem = this.ownerMenuItems.find((item) => item.id === ownerId);
    return foundOwnerItem && foundOwnerItem.checked;
  }

  isTemperatureMenuItemChecked(temperature) {
    const foundTemperatureItem = this.temperatureMenuItems.find((item) => item.name === temperature);
    return foundTemperatureItem && foundTemperatureItem.checked;
  }

  isActiveMenuItemChecked(str) {
    // str should be 'Active' or 'Inactive'
    const foundActiveItem = this.activeMenuItems.find((item) => item.name === str);
    return foundActiveItem && foundActiveItem.checked;
  }

  handleFilterChange = (stateVariableName) => (event) => {
    const newValue = event.target.checked;
    this.props.updateContactsFilter({ filterItemName: stateVariableName, value: newValue });
    this.setState({ [stateVariableName]: newValue });
  };

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = 'desc';

    if (this.state.orderBy === property && this.state.order === 'desc') {
      order = 'asc';
    }

    this.setState({ order, orderBy });
  };

  handleContactClick = (event, firmId, contactId) => {
    this.props.setIntendedReferrerCompanyId(firmId);
    this.props.setIntendedReferrerContactId(contactId);
    this.setState({ redirect: 'referrerContact' });
  };

  applyFilter(entries) {
    return entries.filter((e) => this.passFilter(e));
  }

  passFilter(entry) {
    return (
      entry &&
      this.isFirmTypeMenuItemChecked(entry.firmType) &&
      this.isOwnerMenuItemChecked(entry.ownerId) &&
      this.isTemperatureMenuItemChecked(entry.temperature) &&
      this.isActiveMenuItemChecked(entry.inactive ? 'Inactive' : 'Active')
    );
  }

  showNewContactForm = () => {
    this.setState({ showNewContactModal: true });
  };

  handleNewContactSuccess(visibleFirmId) {
    this.setState({ showNewContactModal: false });
  }

  handleNewContactModalClose() {
    this.setState({ showNewContactModal: false });
  }

  produceUnaffiliatedContactEntries() {
    const contactIdNamePairs = DA.getUnaffiliatedContacts();
    const entries = contactIdNamePairs.map((pair) => this.convertUnaffiliatedContactToContactEntry(pair));
    return entries;
  }

  convertUnaffiliatedContactToContactEntry(contactIdNamePair) {
    const contactId = contactIdNamePair.id;
    const person = DA.getPersonById(contactId);
    const lastContact = person.latestContactDate;
    const daysSince = Math.trunc(DateTime.local().diff(lastContact).as('days'));
    return {
      personId: contactId,
      name: this.getNameForPersonWithId(contactId),
      firmType: '[None]',
      companyName: '[None]',
      daysSince: daysSince,
      lastContact: lastContact,
      firmId: 0,
      inactive: person.inactive,
      ownerId: person.ownerId,
      temperature: person.temperature,
    };
  }

  produceContactEntriesFromCompanyIds(companyIds) {
    let entries = [];
    const companies = companyIds.map((cid) => DA.getFirmById(cid));
    companies.forEach((c) => {
      if (c) entries = entries.concat(this.contactEntriesForCompany(c));
    });
    return entries;
  }

  contactEntriesForCompany(company) {
    if (!company.contactIds) {
      console.log(`Company '${company.name} (id:${company.firmId}) has undefined contacts`);
    }

    const contactIds = company.contactIds || [];

    return contactIds.map((contactId) => this.produceContactEntryForCompanyAndContactId(company, contactId));
  }

  produceContactEntryForCompanyAndContactId(company, contactId) {
    const contact = DA.getPersonById(contactId);
    const lastContact = contact.latestContactDate;
    const daysSince = Math.trunc(DateTime.local().diff(lastContact).as('days'));
    return {
      personId: contactId,
      name: this.computeContactItemText([contactId]),
      firmType: company.firmType,
      companyName: company.name,
      daysSince: daysSince,
      lastContact: lastContact,
      firmId: company.firmId,
      inactive: contact.inactive,
      ownerId: contact.ownerId,
      temperature: contact.temperature,
    };
  }

  computeContactItemText(contactIds) {
    if (!contactIds) {
      contactIds = [];
    }
    if (contactIds.length === 0) {
      return '<None listed yet>';
    } else {
      const firstContactId = contactIds[0];
      const firstContactName = this.getNameForPersonWithId(firstContactId);
      if (contactIds.length === 1) {
        return firstContactName;
      } else {
        return firstContactName + ' ...';
      }
    }
  }

  getNameForPersonWithId(personId) {
    const person = DA.getPersonById(personId);
    return person.preferredFullName;
  }

  addOwnerNameToContact(contact) {
    const ownerId = contact.ownerId;
    let ownerName = '';

    if (ownerId) {
      const owner = DA.getPersonById(ownerId);
      ownerName = owner ? owner.preferredFullName : 'Unassigned';
    } else {
      ownerName = 'Unassigned';
    }

    return { ...contact, ownerName };
  }

  handleCompanyClick = (firmId) => {
    if (firmId > 0) {
      this.props.setIntendedReferrerCompanyId(firmId);
      this.setState({ redirect: 'referrerOrg' });
    }
  };

  maxPhonesPerLine = 0;
  maxAddressesPerLine = 0;

  downloadContacts = (filteredContacts) => {
    this.maxPhonesPerLine = 0;
    this.maxAddressesPerLine = 0;
    const { order, orderBy } = this.state;
    const contacts = maybeSplitAndStableSort(filteredContacts, getSorting(order, orderBy), orderBy);
    this.computeMaxesPerLine(contacts);
    const lines = [];
    contacts.forEach((contact, i) => lines.push(this.lineForContact(contact, i)));
    lines.unshift(this.createHeaderLine());
    const fileContent = lines.join('\n');

    const blob = new Blob([fileContent], { type: 'text/comma-separated-values' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    a.click();
  };

  computeMaxesPerLine(contacts) {
    contacts.forEach((contact) => {
      const person = DA.getPersonById(contact.personId);
      const phones = person.phoneNumbers;
      if (phones.length > this.maxPhonesPerLine) {
        this.maxPhonesPerLine = phones.length;
      }
      const addresses = person.addresses;
      if (addresses.length > this.maxAddressesPerLine) {
        this.maxAddressesPerLine = addresses.length;
      }
    });
  }

  createHeaderLine() {
    let line = 'Count,Name,Company,Email';
    if (this.maxPhonesPerLine < 2) {
      line += ',Phone';
    } else {
      for (let i = 1; i <= this.maxPhonesPerLine; i++) {
        line += ',Phone ' + i;
      }
    }
    if (this.maxAddressesPerLine < 2) {
      line += ',Address,City,State,Zip';
    } else {
      for (let i = 1; i <= this.maxAddressesPerLine; i++) {
        line += ',Address ' + i;
        line += ',City ' + i;
        line += ',State ' + i;
        line += ',Zip ' + i;
      }
    }
    return line;
  }

  lineForContact(contact, i) {
    const person = DA.getPersonById(contact.personId);
    const name = this.csvEscapedValue(contact.name);
    const company = this.csvEscapedValue(contact.companyName);
    const email = this.csvEscapedValue(person.email);
    let line = `${i + 1},${name},${company},${email}`;
    const phoneNumbers = person.phoneNumbers;
    phoneNumbers.forEach((phoneNumberObj) => {
      line += ',' + this.csvEscapedValue(this.phoneNumberStringForPhoneNumberObject(phoneNumberObj));
    });
    const nPhoneNumbersEmitted = phoneNumbers.length;
    const nEmptyPhoneNumbers = this.maxPhonesPerLine - nPhoneNumbersEmitted;
    for (let i = 0; i < nEmptyPhoneNumbers; i++) {
      line += ',';
    }

    const addresses = person.addresses || [];

    addresses.forEach((addressObj) => {
      line += ',' + this.csvEscapedValue(addressObj.line1);
      line += ',' + this.csvEscapedValue(addressObj.city);
      line += ',' + this.csvEscapedValue(addressObj.state);
      line += ',' + this.csvEscapedValue(addressObj.zip);
    });
    const nAddressesEmitted = addresses.length;
    const nEmptyAddresses = this.maxAddressesPerLine - nAddressesEmitted;
    for (let i = 0; i < nEmptyAddresses; i++) {
      line += ',';
    }
    return line;
  }

  phoneNumberStringForPhoneNumberObject(phoneNumberObj) {
    const rolesStr = phoneNumberObj.roles;
    if (!rolesStr) {
      console.log(`PhoneNumber object with ID ${phoneNumberObj.phoneId} has null or empty roles string`);
      return phoneNumberObj.number;
    }
    const rolesArr = rolesStr.split(',');
    const firstLetterArray = rolesArr.map((roleStr) => roleStr.substring(0, 1).toLowerCase());
    const shortRolesStr = firstLetterArray.join(',');
    const parenthesizedShorRolesStr = '(' + shortRolesStr + ')';
    return phoneNumberObj.number + ' ' + parenthesizedShorRolesStr;
  }

  csvEscapedValue(value) {
    if (!value) {
      return '';
    }
    let needsQuoteMarks = false;
    if (value.indexOf('"') >= 0) {
      needsQuoteMarks = true;
      value = value.replace('"', '""');
    }
    if (value.indexOf('\n') >= 0 || value.indexOf(',') >= 0) {
      needsQuoteMarks = true;
    }
    return needsQuoteMarks ? '"' + value + '"' : value;
  }

  handleFilterItemChange = (itemIndex) => {
    const copiedList = this.state.filterItemList.slice(0);
    copiedList[itemIndex].checked = !this.state.filterItemList[itemIndex].checked;

    this.saveMenuItemState(copiedList[itemIndex]);

    const allSelected = allMenuItemsAreChecked(copiedList.slice(1));
    const selectionButtonLabel = allSelected ? 'Unselect All' : 'Select All';
    copiedList[0].name = selectionButtonLabel;

    this.setState({ filterItemList: copiedList });
  };

  shouldHighlightFilterIcon(columnId) {
    if (columnId === 'firmType') {
      return this.shouldHighlightFirmTypeFilterIcon();
    } else if (columnId === 'temperature') {
      return this.shouldHighlightTemperatureFilterIcon();
    } else if (columnId === 'ownerName') {
      return this.shouldHighlightOwnerNameFilterIcon();
    } else if (columnId === 'active') {
      return this.shouldHighlightActiveFilterIcon();
    }
  }

  shouldHighlightFirmTypeFilterIcon() {
    return !filterIsInDefaultState(this.firmTypeMenuItems, this.defaultFirmTypeMenuItems);
  }

  shouldHighlightOwnerNameFilterIcon() {
    return !filterIsInDefaultState(this.ownerMenuItems, this.defaultOwnerMenuItems);
  }

  shouldHighlightTemperatureFilterIcon() {
    return !filterIsInDefaultState(this.temperatureMenuItems, this.defaultTemperatureMenuItems);
  }

  shouldHighlightActiveFilterIcon() {
    return !filterIsInDefaultState(this.activeMenuItems, this.defaultActiveMenuItems);
  }

  saveMenuItemState(item) {
    const columnId = this.filteredColumnId;
    if (columnId === 'ownerName') {
      const payload = {
        pageName: 'referralContactsPage',
        filterName: columnId,
        personId: item.id,
        personName: item.name,
        value: item.checked,
      };
      this.props.setPersonFilterState(payload);
    } else if (columnId === 'temperature' || columnId === 'active' || columnId === 'firmType') {
      const payload = {
        pageName: 'referralContactsPage',
        filterName: columnId,
        propertyName: item.name,
        value: item.checked,
      };
      this.props.setPropertyFilterState(payload);
    }
  }

  handleFilterMenuClickAway = () => () => {
    this.setState({ showFilter: false });
  };

  handleSelectAllButtonClick = (itemName) => {
    const copiedList = this.state.filterItemList.slice(0);
    let shouldBeChecked;
    if (itemName === 'Select All') {
      copiedList[0].name = 'Unselect All';
      shouldBeChecked = true;
    } else {
      copiedList[0].name = 'Select All';
      shouldBeChecked = false;
    }

    copiedList.forEach((item) => {
      if (!item.name.endsWith('elect All')) {
        item.checked = shouldBeChecked;
      }
      this.saveMenuItemState(item);
    });

    this.setState({ filterItemList: copiedList });
  };

  showFilterMenu = (event, columnId) => {
    const { clientX, clientY } = event;
    let filterItemList = [];

    if (columnId === 'firmType') {
      filterItemList = this.firmTypeMenuItems;
    } else if (columnId === 'ownerName') {
      filterItemList = this.ownerMenuItems;
    } else if (columnId === 'temperature') {
      filterItemList = this.temperatureMenuItems;
    } else if (columnId === 'active') {
      filterItemList = this.activeMenuItems;
    }
    this.filteredColumnId = columnId;
    this.setState({
      showFilter: true,
      filterX: clientX,
      filterY: clientY,
      filterItemList,
      filterBy: columnId,
    });
  };

  render() {
    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    const { classes } = this.props;
    const { order, orderBy, filterItemList } = this.state;
    const viewingFirmId = DA.getFirmId();
    let companyIds = DA.getIdsOfFirmsVisibleToFirm(viewingFirmId);
    companyIds = companyIds.filter((cId) => cId !== viewingFirmId);
    const affiliatedEntries = this.produceContactEntriesFromCompanyIds(companyIds);
    const unaffiliatedEntries = this.produceUnaffiliatedContactEntries();
    const contactEntries = [...affiliatedEntries, ...unaffiliatedEntries];
    const augmentedContacts = contactEntries.map((contact) => this.addOwnerNameToContact(contact));
    const filteredContacts = this.applyFilter(augmentedContacts);
    const isFiltered =
      !filterIsInDefaultState(this.firmTypeMenuItems, this.defaultFirmTypeMenuItems) ||
      !filterIsInDefaultState(this.ownerMenuItems, this.defaultOwnerMenuItems) ||
      !filterIsInDefaultState(this.temperatureMenuItems, this.defaultTemperatureMenuItems) ||
      !filterIsInDefaultState(this.activeMenuItems, this.defaultActiveMenuItems);
    const sortedContacts = maybeSplitAndStableSort(filteredContacts, getSorting(order, orderBy), orderBy);
    const summary = `${isFiltered ? 'Filtered' : 'All'} Contacts (${filteredContacts.length})`;
    const StyledFilterDialog = styled(Dialog)`
      .MuiBackdrop-root {
        background-color: transparent;
      },
    `;

    const filterPosition = {
      position: 'absolute',
      left: this.state.filterX + 'px',
      top: this.state.filterY + 'px',
      margin: '5px',
      boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
    };
    const filterStyle = { style: filterPosition };

    return (
      <Paper className={classes.root}>
        {this.state.showNewContactModal && (
          <IcfUnaffiliatedContactModal
            onSuccess={(visibleFirmId) => this.handleNewContactSuccess(visibleFirmId)}
            onCancel={() => this.handleNewContactModalClose()}
          />
        )}

        <ContactTableAppBar
          summary={summary}
          newButtonLabel="New 'Unaffiliated' Contact"
          newButtonFunc={() => this.showNewContactForm()}
          contacts={filteredContacts}
          onAutocompleteChange={(contact) => this.handleContactClick(null, contact.referralFirmId, contact.personId)}
          downloadFunc={() => this.downloadContacts(filteredContacts)}
        />
        <div className={classes.tableWrapper}>
          <Table stickyHeader className={classes.table} aria-labelledby="tableTitle">
            <StyledTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={this.handleRequestSort}
              onRequestFilter={this.showFilterMenu}
              shouldHighlightFilterIcon={(columnId) => this.shouldHighlightFilterIcon(columnId)}
            />
            <TableBody>
              {sortedContacts.map((entry, i) => {
                const companyShouldBeClickable = !entry.inactive && entry.companyName !== '[None]';
                return (
                  <TableRow
                    tabIndex={-1}
                    key={entry.personId}
                    id={'contact-row-' + entry.personId}
                    style={{ backgroundColor: i % 2 ? '#f7f7f7' : '#fff' }}
                  >
                    <TableCell
                      className={cx([classes.clickableCell, { [classes.inactiveName]: entry.inactive }])}
                      component="th"
                      scope="row"
                      padding="normal"
                      onClick={(event) => this.handleContactClick(event, entry.firmId, entry.personId)}
                    >
                      {entry.name}
                    </TableCell>
                    <TableCell className={cx([{ [classes.inactiveName]: !companyShouldBeClickable }])}>
                      {entry.firmType}
                    </TableCell>
                    <TableCell
                      className={cx(
                        { [classes.clickableCell]: companyShouldBeClickable },
                        { [classes.inactiveName]: !companyShouldBeClickable }
                      )}
                      onClick={() => this.handleCompanyClick(entry.firmId)}
                    >
                      {entry.companyName}
                    </TableCell>
                    <TableCell>{entry.ownerName}</TableCell>
                    <TableCell>{entry.temperature}</TableCell>
                    <TableCell>{entry.inactive ? 'Inactive' : 'Active'}</TableCell>
                    <TableCell align="right">{entry.daysSince > 1000 ? '' : entry.daysSince}</TableCell>
                    <TableCell align="right">
                      {entry.daysSince > 1000 ? '' : dateStringForDateTime(entry.lastContact)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <StyledFilterDialog
          open={this.state.showFilter}
          PaperComponent={Paper}
          PaperProps={filterStyle}
          onEntered={handleEntered}
        >
          <ClickAwayListener onClickAway={this.handleFilterMenuClickAway()}>
            <List>
              {filterItemList.map((item, i) => (
                <ListItem className={classes.ownerListItem} key={i}>
                  <Typography variant="caption" display="block" gutterBottom>
                    {i === 0 && (
                      <Button
                        className={classes.selectAllButton}
                        onClick={() => this.handleSelectAllButtonClick(item.name)}
                      >
                        {item.name}
                      </Button>
                    )}
                    {i > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={item.checked}
                            onChange={() => this.handleFilterItemChange(i)}
                            value={item.name}
                            color="primary"
                          />
                        }
                        label={item.name}
                        className={classes.ownerLabel}
                      />
                    )}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </ClickAwayListener>
        </StyledFilterDialog>
      </Paper>
    );
  }
}

ContactTable.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  reduxFilterMenuState: state.intendedInfo.contactsPageFilter,
  reduxPropertyFiltersState: state.filterStates.propertyFilters,
});

const actionProducers = {
  setApiResponseInfo,
  setIntendedReferrerContactId,
  setIntendedReferrerCompanyId,
  setPersonFilterState,
  setPropertyFilterState,
  updateContactsFilter,
};

export default connect(mapStateToProps, actionProducers)(withStyles(styles)(ContactTable));
